/**
 * Listener (User) Action Creators
 *
 * @module actions/Listener
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { type Dispatch } from 'redux';
import { createAction } from 'typesafe-actions';

import { openModal } from '../actions/Modal';
import { messages } from '../constants/Anon';
import { ListenerActions } from '../constants/Listener';
import RegisterActions from '../constants/RegisterActions';

/**
 * Called to reset register state that meaningful during register operation
 */
export const registerReset = createAction(RegisterActions.REGISTER_RESET)();
export type RegisterResetAction = ReturnType<typeof registerReset>;

/**
 * Called to clear out the error code from state
 */
export const clearRegisterError = createAction(RegisterActions.REGISTER_CLEAR_ERROR)();
export type RegisterClearErrorAction = ReturnType<typeof clearRegisterError>;

/**
 * Registration attempt is not valid at this time
 */
export const registerAttemptBlocked = createAction(RegisterActions.REGISTER_BLOCKED)();
export type RegisterBlockedAction = ReturnType<typeof registerAttemptBlocked>;

type SetHasGivenFeedbackParams = boolean;
interface SetHasGivenFeedbackPayload {
  isPositive: SetHasGivenFeedbackParams;
}

export const setHasGivenFeedback = createAction(
  ListenerActions.SET_HAS_GIVEN_FEEDBACK,
  (isPositive: SetHasGivenFeedbackParams) => ({ isPositive }),
)<SetHasGivenFeedbackPayload>();

export type SetHasGivenFeedbackAction = ReturnType<typeof setHasGivenFeedback>;

const pathMessageMap = {
  '/account/sign-in': messages.alreadySignedIn,
  '/account/register': messages.alreadyRegistered,
  '/account/reset-password': messages.resetPasswordLogOut,
};
/**
 * Show listener coachmark after redirecting based on logged-in/registered status
 *
 * @param {string} path - where the user was redirected away from
 * @returns {object} - action object
 */
export const showRedirectedListenerCoachmark = (path: keyof typeof pathMessageMap) => {
  return (dispatch: Dispatch<$TSFixMe>): $TSFixMe => {
    const message = pathMessageMap[path];
    const formattedMessage = <FormattedMessage {...message} />;
    dispatch(
      openModal({
        modalType: 'ModalCoachMark',
        title: '',
        body: formattedMessage,
        buttons: [],
      }),
    );
  };
};

type ListenerAuthenticatedParams = $TSFixMe;
interface ListenerAuthenticatedPayload {
  authType: ListenerAuthenticatedParams;
}

export const listenerAuthenticated = createAction(
  ListenerActions.LISTENER_AUTHENTICATED,
  (authType: ListenerAuthenticatedParams) => ({ authType }),
)<ListenerAuthenticatedPayload>();

export type ListenerAuthenticatedAction = ReturnType<typeof listenerAuthenticated>;
